import React, { FunctionComponent, useMemo, useState, useCallback } from 'react';
import clsx from 'clsx';
import { Box, Menu, MenuItem, Button, useMediaQuery, useTheme, Typography } from '@material-ui/core';
import { ChevronLeft } from '@material-ui/icons';
import NavigationElement from '../navigationElement/NavigationElement';
import sectionNavigationStyles from './sectionNavigationStyles';
import INavigationItem from '../../model/INavigationItem';
import { CustomiseCategoriesIds } from '../../../constants/customiseCategoriesIds';
import useNavigationLabel from '../hooks/useNavigationLabel';
import { ISpcTheme } from '../../../../common/theme/ISpcTheme';
import CheckIcon from '@material-ui/icons/Check';

export interface ISectionNavigationProps {
  /**
   * Items that are rendered into navigation bar ( name + completed )
   */
  items: INavigationItem[];
  /**
   * Click event with parameter of the item clicked
   * @param item
   */
  onClick: (item: string) => void;
  /**
   * Check if the label is translated
   */
  withTranslation?: boolean;
  isGraphql?: boolean;
  trimLabel?: string;
  /**
   * Currently active item
   */
  activeItem: string;
  className?: string;
}

const SectionNavigation: FunctionComponent<ISectionNavigationProps> = ({
  items,
  onClick,
  withTranslation,
  isGraphql,
  trimLabel,
  activeItem,
  className,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);
  const theme = useTheme();
  const {
    spc: {
      customise: { displayNavigationDropdownMenu, displayNavigationDropdownCheck },
    },
  } = useTheme<ISpcTheme>();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { renderLabel } = useNavigationLabel(isGraphql, withTranslation);

  const hasMultipleSections = items.length > 1;

  const currentItemIndex = useMemo(
    () => items.indexOf(items.find(item => item.name === activeItem)),
    [items, activeItem],
  );

  const handleClick = useCallback((target: string) => {
    onClick(target);
    handleClose();
  }, []);

  const handleOpen = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const styles = sectionNavigationStyles();

  if (isMobile && displayNavigationDropdownMenu) {
    return (
      <Box className={styles.root}>
        <Button
          className={styles.toggleButton}
          disableRipple={true}
          disableElevation={true}
          disabled={!hasMultipleSections}
          id="section-navigation-button"
          aria-controls={isOpen ? 'section-navigation-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={isOpen ? 'true' : undefined}
          classes={{
            disabled: styles.toggleButtonDisabled,
          }}
          onClick={handleOpen}
          {...(hasMultipleSections && {
            endIcon: (
              <ChevronLeft
                className={clsx([
                  styles.endIcon,
                  {
                    [styles.endIconOpen]: isOpen,
                  },
                ])}
              />
            ),
          })}
        >
          <Typography component="span" className={clsx(styles.label, { [styles.customiseLabel]: !trimLabel })}>
            {displayNavigationDropdownCheck && <CheckIcon className={styles.checkIcon} />}
            {hasMultipleSections && (
              <Typography component="span" className={styles.currentIndex}>
                {currentItemIndex + 1}
              </Typography>
            )}
            {renderLabel(items?.[currentItemIndex]?.label, activeItem, trimLabel)}
          </Typography>
        </Button>
        <Menu
          className={styles.dropdownMenu}
          id="section-navigation-menu"
          anchorEl={anchorEl}
          anchorOrigin={{
            horizontal: 'center',
            vertical: 'bottom',
          }}
          open={isOpen}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'section-navigation-button',
            classes: {
              root: styles.dropdownMenuList,
            },
          }}
        >
          {items.map((navigationItem, index) => (
            <MenuItem className={styles.menuItem} key={navigationItem.name}>
              <NavigationElement
                onClick={handleClick}
                completed={currentItemIndex > index}
                activeItem={activeItem}
                name={navigationItem.name}
                label={navigationItem.label}
                index={index}
                withTranslation={withTranslation}
                disabledItem={navigationItem.disabled}
                testId={CustomiseCategoriesIds[navigationItem.name] ?? navigationItem.name}
                hideItemNumber={items.length < 2}
                trimLabel={trimLabel}
                isGraphql={isGraphql}
              />
            </MenuItem>
          ))}
        </Menu>
      </Box>
    );
  }

  return (
    <Box className={clsx([styles.buttonsContainer, className])}>
      {items.map((navigationItem, index) => (
        <NavigationElement
          key={navigationItem.name}
          onClick={onClick}
          completed={currentItemIndex > index}
          activeItem={activeItem}
          name={navigationItem.name}
          label={navigationItem.label}
          index={index}
          withTranslation={withTranslation}
          disabledItem={navigationItem.disabled}
          testId={CustomiseCategoriesIds[navigationItem.name] ?? navigationItem.name}
          hideItemNumber={items.length < 2}
          trimLabel={trimLabel}
          isGraphql={isGraphql}
        />
      ))}
    </Box>
  );
};

export default SectionNavigation;
